// src/features/communities/meta.ts — static tables + types for Communities.
import type { Post } from '@/core/models/post';
import { hasMedia } from '@/components/PostCard/postImages';

export type DirTab = 'discover' | 'following' | 'invitations';
export type SubTab = 'posts' | 'about' | 'members' | 'media' | 'events' | 'rules';

export interface DirTabDef { id: DirTab; label: string; }
export interface SubTabDef { id: SubTab; label: string; }

export const DIR_TABS: DirTabDef[] = [
  { id: 'discover',     label: 'Discover' },
  { id: 'following',    label: 'Following' },
  { id: 'invitations',  label: 'Invitations' },
];

export const SUB_TABS: SubTabDef[] = [
  { id: 'posts',   label: 'Posts' },
  { id: 'about',   label: 'About' },
  { id: 'members', label: 'Members' },
  { id: 'media',   label: 'Media' },
  { id: 'events',  label: 'Events' },
  { id: 'rules',   label: 'Rules' },
];

// Cover gradients are hue-parameterized off the premium-purple identity (violet
// accent, since cd47be5) rather than the flat "void+teal" surface the comment
// here used to describe. Every gradient shares the same dark base and the same
// fixed saturation/lightness constants below — that fixed S/L is the de-rainbow
// guarantee from 2d44347: covers stay in the dark-surface family and the
// community name stays legible no matter which hue is picked. Category identity
// legitimately comes back from the cover (varying hue), while the darkness
// constraint lives in the formula, not in every cover being identical.
const BASE_FROM = '#0E141A';
const MID_SATURATION = 30; // %
const MID_LIGHTNESS = 12; // %
const ACCENT_SATURATION = 70; // %
const ACCENT_LIGHTNESS = 60; // %
const ACCENT_ALPHA = 0.12;

/** Wraps (modulo 360) and validates an arbitrary input into a 0-359 integer hue, falling back to 0. */
function normalizeHue(hue: number): number {
  if (!Number.isFinite(hue)) return 0;
  const wrapped = Math.round(hue) % 360;
  return wrapped < 0 ? wrapped + 360 : wrapped;
}

/**
 * The single gradient formula every cover (preset or custom) is derived from.
 * Fixed dark saturation/lightness (see constants above) guarantees the result
 * always reads as a moody dark surface — only the hue varies.
 */
export function gradientForHue(hue: number): string {
  const h = normalizeHue(hue);
  const mid = `hsl(${h}, ${MID_SATURATION}%, ${MID_LIGHTNESS}%)`;
  const accent = `hsla(${h}, ${ACCENT_SATURATION}%, ${ACCENT_LIGHTNESS}%, ${ACCENT_ALPHA})`;
  return `linear-gradient(135deg, ${BASE_FROM} 0%, ${mid} 70%, ${accent} 100%)`;
}

interface TonePreset { hue: number; label: string; }

// Six spread hues sampling gradientForHue — violet leads to match the brand
// accent, the rest fan out around the wheel so presets stay pairwise distinct.
const TONE_PRESETS: Record<string, TonePreset> = {
  a: { hue: 262, label: 'violet' },
  b: { hue: 232, label: 'indigo' },
  c: { hue: 185, label: 'cyan-teal' },
  d: { hue: 150, label: 'green' },
  e: { hue: 38,  label: 'amber' },
  f: { hue: 345, label: 'rose' },
};

const TONE_A_GRADIENT = gradientForHue(TONE_PRESETS.a!.hue);

export const COVER_GRADIENTS: Record<string, string> = Object.fromEntries(
  Object.entries(TONE_PRESETS).map(([id, { hue }]) => [id, gradientForHue(hue)]),
);

export function toneLabel(tone: string): string {
  return TONE_PRESETS[tone]?.label ?? TONE_PRESETS.a!.label;
}

export const COVER_TONES = ['a', 'b', 'c', 'd', 'e', 'f'] as const;

// Starting position for the custom-hue slider before a user has touched it —
// an arbitrary cyan-ish point on the wheel, distinct from every preset hue.
export const DEFAULT_CUSTOM_HUE = 210;

const HUE_PREFIX = 'hue:';

/** Build the stored-value form for a custom hue selection. */
export function hueToneValue(hue: number): string {
  return `${HUE_PREFIX}${normalizeHue(hue)}`;
}

/** Parse a `hue:<0-359>` stored value into its numeric hue, or null if malformed. */
export function parseHueTone(tone: string | undefined): number | null {
  if (!tone || !tone.startsWith(HUE_PREFIX)) return null;
  const raw = tone.slice(HUE_PREFIX.length);
  if (!/^\d{1,3}$/.test(raw)) return null;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 0 || n > 359) return null;
  return n;
}

/**
 * coverTone (and the create-dialog's local `tone` state) is a single string
 * column that stores one of THREE forms:
 *   1. an http(s) URL — an uploaded cover image (see isHttpUrl; callers render
 *      it as a background-image, not through this function).
 *   2. `hue:<0-359>` — a custom colour-wheel selection, e.g. "hue:210".
 *   3. a preset tone id ('a'-'f') — one of the six named presets above.
 * Anything else (empty, corrupt, unknown id) falls back to tone 'a'.
 * This is the single place that resolves form 2 and 3 into CSS; the two
 * callers that still branch on isHttpUrl themselves for form 1 (they need the
 * raw URL, not CSS) are CommunityBanner and PopularCommunityCard.
 */
export function coverBackground(tone: string | undefined): string {
  const hue = parseHueTone(tone);
  if (hue !== null) return gradientForHue(hue);
  // Object.hasOwn (not `tone in COVER_GRADIENTS`) — `in` walks the prototype
  // chain, so a stray tone value like 'toString' or 'constructor' would
  // resolve to a Function rather than falling back to tone 'a'.
  if (tone && Object.hasOwn(COVER_GRADIENTS, tone)) return COVER_GRADIENTS[tone]!;
  return TONE_A_GRADIENT;
}

// Used by MediaTab — a post counts as media when it has a media field.
export function postsWithMedia(posts: Post[]): Post[] {
  return posts.filter((p) => hasMedia(p));
}
